import React from 'react';
import { Card, Badge } from 'react-bootstrap';
import { useNavigate } from 'react-router-dom';
import { generateSlug } from '../utils/slugUtils';
import { getLocationStatusBadge, formatExpectedDate, formatClosedDate } from '../utils/statusUtils';
import { ownershipStatus } from '../utils/ownership';
import { openStatus } from '../utils/openStatus';

/**
 * Shared restaurant summary card (#67). Extracted verbatim from RestaurantList's
 * canonical card so every surface (list, profile tabs, …) renders the same
 * badges, image collage, and layout — killing the drift where features added to
 * one card (fav/wishlist #61, open-now #62) never reached the copies.
 *
 * `favoriteIds` / `wishlistIds` are Sets from `useSavedRestaurantIds()`. The
 * PARENT calls that hook once and passes them down, because the hook fires two
 * API calls per mount — calling it per-card would mean ~2×N requests for a list.
 * `userLocation` (optional) enables the distance line.
 *
 * Renders the `<Card>` only; the caller supplies its own grid `<Col>` wrapper.
 */
export default function RestaurantCard({
  restaurant,
  favoriteIds = new Set(),
  wishlistIds = new Set(),
  userLocation = null,
  note = null,
}) {
  const navigate = useNavigate();

  // #158: which of the three "private" states this is (null when public).
  const status = ownershipStatus(restaurant);

  const cuisines = restaurant.cuisines?.slice(0, 2).map((c) => c.name).join(' • ') || '';
  // Favorites/wishlist payloads carry a single `main_location` instead of the
  // full `locations` array — fall back to it so those surfaces still show a place.
  const displayLocation =
    restaurant.closestLocation ||
    restaurant.locations?.find((loc) => loc.is_main_location) ||
    restaurant.locations?.[0] ||
    restaurant.main_location ||
    null;
  const locationText = displayLocation
    ? displayLocation.city + (displayLocation.state ? `, ${displayLocation.state}` : '')
    : '';
  const statusBadge = displayLocation ? getLocationStatusBadge(displayLocation) : null;
  const cardOpacity = statusBadge ? statusBadge.opacity : 1;
  const cardBorderLeft = statusBadge ? `4px solid ${statusBadge.bgColor}` : '';

  return (
    <Card
      className="shadow-sm restaurant-card"
      style={{
        cursor: 'pointer',
        height: '100%',
        opacity: cardOpacity,
        borderLeft: cardBorderLeft,
      }}
      onClick={() => navigate(`/restaurant/${restaurant.id}/${generateSlug(restaurant.name)}`)}
    >
      <Card.Body className="p-3">
        {/* Title and Rating Row */}
        <div className="d-flex justify-content-between align-items-start mb-1">
          <div className="d-flex align-items-center gap-2 flex-wrap">
            <h6 className="mb-0 fw-bold text-primary" style={{ fontSize: '1rem' }}>
              {restaurant.name}
            </h6>
            {/* Favorite / Wishlist badges (#61) — parity with the iOS list rows
                and the watch's Open Nearby rows (#58). */}
            {favoriteIds.has(restaurant.id) && (
              <span title="Favorited" aria-label="Favorited" style={{ fontSize: '0.8rem' }}>❤️</span>
            )}
            {wishlistIds.has(restaurant.id) && (
              <span title="On your wishlist" aria-label="On your wishlist" style={{ fontSize: '0.8rem' }}>📌</span>
            )}
            {/* Open-now badge (#62) — server-computed, timezone-aware (#39).
                #284: now says how much longer ("Open until 9 PM" / "Closes in 45m"),
                because being open this instant does not answer "will it still be
                open when I get there". openStatus() owns the four-state mapping so
                this can't render a 24-hour place as "hours unknown". */}
            {(() => {
              const status = openStatus(restaurant);
              if (!status) return null;
              return (
                <Badge
                  bg={status.variant}
                  title={status.detail}
                  style={{ fontSize: '0.6rem', padding: '0.25rem 0.4rem' }}
                >
                  {status.label}
                </Badge>
              );
            })()}
            {/* Show status badge if all locations have the same status */}
            {(() => {
              if (!restaurant.locations || restaurant.locations.length === 0) return null;

              const firstLocation = restaurant.locations[0];
              const allSameStatus = restaurant.locations.every(
                (loc) =>
                  loc.is_closed === firstLocation.is_closed &&
                  loc.closure_reason === firstLocation.closure_reason
              );

              if (allSameStatus) {
                const badge = getLocationStatusBadge(firstLocation);
                if (badge) {
                  return (
                    <Badge
                      style={{
                        fontSize: '0.65rem',
                        padding: '0.25rem 0.5rem',
                        backgroundColor: badge.bgColor,
                        color: badge.textColor || '#fff',
                      }}
                    >
                      {badge.text}
                    </Badge>
                  );
                }
              }
              return null;
            })()}
          </div>
          {/* Placeholder for rating - will show once reviews are loaded */}
          <small className="text-muted">⭐ -</small>
        </div>

        {/* #158: the three private states, from the shared helper. Was a
            copy-pasted three-way conditional that badged "an admin unpublished
            this" and "just for me" identically. Null for public → no badge. */}
        {status && (
          <div className="mb-2">
            <Badge
              bg={status.variant}
              text={status.variant === 'warning' ? 'dark' : undefined}
              className="d-flex align-items-center gap-1"
              style={{ width: 'fit-content' }}
            >
              {status.icon} {status.label}
            </Badge>
            <small className="text-muted d-block mt-1">{status.hint}</small>
          </div>
        )}

        {/* Cuisine and Location */}
        <div className="text-muted small mb-1">
          {cuisines && <span>{cuisines}</span>}
          {cuisines && locationText && <span> • </span>}
          {locationText && <span>{locationText}</span>}
        </div>

        {/* Optional per-item note (e.g. a wishlist note) */}
        {note && (
          <div
            className="text-muted small mb-1"
            style={{ fontStyle: 'italic', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}
          >
            "{note}"
          </div>
        )}

        {/* Distance if available */}
        {userLocation && restaurant.distance !== null && restaurant.distance !== undefined && (
          <div className="small mb-2">
            {(() => {
              const locationStatusBadge = displayLocation ? getLocationStatusBadge(displayLocation) : null;
              const hasStatus = locationStatusBadge !== null;

              return (
                <>
                  <span className={hasStatus ? 'text-muted' : 'text-success'}>
                    {restaurant.distance.toFixed(1)} mi away
                    {displayLocation && displayLocation.name && ` (${displayLocation.name})`}
                    {locationStatusBadge && (
                      <Badge
                        className="ms-2"
                        style={{
                          fontSize: '0.65rem',
                          backgroundColor: locationStatusBadge.bgColor,
                          color: locationStatusBadge.textColor || '#fff',
                        }}
                      >
                        {locationStatusBadge.text}
                      </Badge>
                    )}
                  </span>
                  {restaurant.locations && restaurant.locations.length > 1 && (
                    <Badge bg="info" className="ms-2" style={{ fontSize: '0.7rem' }}>
                      {restaurant.locations.length} locations
                    </Badge>
                  )}
                  {displayLocation && displayLocation.is_closed && displayLocation.closed_date && (
                    <div className="text-muted" style={{ fontSize: '0.75rem' }}>
                      Closed: {formatClosedDate(displayLocation.closed_date)}
                    </div>
                  )}
                  {locationStatusBadge && locationStatusBadge.showDate && displayLocation.expected_opening_date && (
                    <div style={{ fontSize: '0.75rem', fontWeight: '600', color: locationStatusBadge.bgColor }}>
                      {locationStatusBadge.dateLabel}: {formatExpectedDate(displayLocation.expected_opening_date)}
                    </div>
                  )}
                </>
              );
            })()}
          </div>
        )}

        {/* Image Thumbnails Row - Prioritize menu item images over restaurant images */}
        <div className="d-flex gap-2 overflow-hidden" style={{ height: '60px' }}>
          {(() => {
            let displayImages = [];

            if (restaurant.menu_item_images && restaurant.menu_item_images.length > 0) {
              displayImages = restaurant.menu_item_images
                .slice()
                .sort((a, b) => a.display_order - b.display_order)
                .slice(0, 4);
            }

            if (displayImages.length < 4 && restaurant.images && restaurant.images.length > 0) {
              const remaining = 4 - displayImages.length;
              const restaurantImages = restaurant.images
                .slice()
                .sort((a, b) => a.display_order - b.display_order)
                .slice(0, remaining);
              displayImages = [...displayImages, ...restaurantImages];
            }

            return displayImages.length > 0 ? (
              displayImages.map((img, idx) => (
                <div
                  key={idx}
                  className="border rounded flex-shrink-0 overflow-hidden"
                  style={{ width: '80px', height: '60px' }}
                >
                  <img
                    src={img.flickr_url}
                    alt={`${restaurant.name} ${idx + 1}`}
                    style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                    onError={(e) => {
                      e.target.style.display = 'none';
                    }}
                  />
                </div>
              ))
            ) : (
              <div
                className="bg-light border rounded flex-fill d-flex align-items-center justify-content-center text-muted"
                style={{ height: '60px' }}
              >
                <span className="small">No photos yet</span>
              </div>
            );
          })()}
        </div>
      </Card.Body>
    </Card>
  );
}
